import React, { useEffect, useMemo, useState } from 'react';
import { SubscriptionService } from '../../core/services';
import { hasRazorpayPlan } from '../../core/services/PaymentService';
import { zapPlanFeatures, zapPlanHeading } from '../../core/utility';
import { SubscriptionPlanFeaturesData } from '../common/settings/subscription/subscription-plan-data';
import { currencySymbol } from '../common/settings/subscription/pricing-plan';
import { CheckoutLogo } from './addon-checkout';
import './plan-checkout.scss';

const subscriptionService: SubscriptionService = new SubscriptionService();

// Line icons (lucide shapes, as in addon-checkout).
const svgProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const CrownIcon = () => (
  <svg width='26' height='26' viewBox='0 0 24 24' {...svgProps}>
    <path d='m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7z' />
    <path d='M5 20h14' />
  </svg>
);

const CheckIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' {...svgProps} strokeWidth={2.5}>
    <path d='M20 6 9 17l-5-5' />
  </svg>
);

const LockIcon = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' {...svgProps}>
    <rect x='3' y='11' width='18' height='11' rx='2' />
    <path d='M7 11V7a5 5 0 0 1 10 0v4' />
  </svg>
);

const ChevronIcon = ({ up }: { up: boolean }) => (
  <svg width='16' height='16' viewBox='0 0 24 24' {...svgProps} style={{ transform: up ? 'rotate(180deg)' : undefined }}>
    <path d='m6 9 6 6 6-6' />
  </svg>
);

/** How a plan bills: "/month" after the price, "Billed monthly", "every month". */
const periodOf = (plan: any): { per: string; billed: string; every: string } => {
  switch (plan?.plan_type) {
    case 'Monthly':
      return { per: 'month', billed: 'Billed monthly', every: 'every month' };
    case 'Annual Plan':
    case 'Annually':
      return { per: 'year', billed: 'Billed yearly', every: 'every year' };
    case 'Half-Yearly':
      return { per: '6 months', billed: 'Billed every 6 months', every: 'every 6 months' };
    case 'Quarterly':
      return { per: '3 months', billed: 'Billed every 3 months', every: 'every 3 months' };
    default: {
      const days = Number(plan?.days) || 30;
      return { per: `${days} days`, billed: `For ${days} days`, every: `every ${days} days` };
    }
  }
};

const isYearly = (plan: any) => ['Annual Plan', 'Annually'].includes(plan?.plan_type);

/** A paid subscription tier — never the free (FOC) plan or the 7-day trial. */
export const isPaidPlan = (plan: any): boolean =>
  Number(plan?.amount) > 0 &&
  !['FOC', 'Trial'].includes(plan?.plan_name) &&
  !['Free', 'Trial', 'Trail'].includes(plan?.user_status);

const money = (plan: any, amount: any) => {
  const n = Number(amount) || 0;
  const locale = String(plan?.currency || 'INR').toUpperCase() === 'INR' ? 'en-IN' : 'en-US';
  return `${currencySymbol(plan)}${n.toLocaleString(locale)}`;
};

interface FeatureGroup {
  title: React.ReactNode;
  items: { text: React.ReactNode; value?: string }[];
}

/** The selected plan's features: the same lists as the web's pricing cards. */
const featureGroupsOf = (plan: any, influencerSide: boolean): FeatureGroup[] => {
  const groups: FeatureGroup[] = [];
  if (influencerSide) {
    const zap = zapPlanFeatures(plan?.entitlements, plan?.plan_name);
    if (zap.length) groups.push({ title: zapPlanHeading(plan?.plan_name), items: zap.map((text) => ({ text })) });
    // Zap-only (foreign) plans carry no marketplace features (as pricing-plan.tsx).
    if (plan?.zap_only) return groups;
  }
  const list: any[] = influencerSide
    ? SubscriptionPlanFeaturesData.influencer.premium_plan_features
    : SubscriptionPlanFeaturesData.brand.premium_plan_features;
  for (const f of list) {
    // Section headings are the rows without a check / value ('border-bottom-0').
    if (f.liClassName === 'border-bottom-0') {
      groups.push({ title: (f.text as any)?.props?.children ?? f.text, items: [] });
    } else if (groups.length) {
      groups[groups.length - 1].items.push({ text: f.text, value: f.value || undefined });
    }
  }
  return groups.filter((g) => g.items.length);
};

const COLLAPSED_FEATURES = 6;

/** While the link's key is read. */
export const PlanCheckoutLoading: React.FC = () => (
  <main className='plan-checkout'>
    <CheckoutLogo />
    <div className='plan-checkout__plans'>
      <div className='plan-checkout__skeleton' />
      <div className='plan-checkout__skeleton' />
    </div>
  </main>
);

interface Props {
  user: any;
  plans: any[] | null;  // null while loading
  plansError: string;
  activePlan: any;      // /get/subscription/days/left row, or null
  loading: boolean;     // a payment is in progress
  onPay: (plan: any, couponCode?: string) => void;
}

/**
 * The app's Premium checkout (/subscription?key=…), opened from the Android app
 * (and the brand app on Android): who is paying comes from the signed key, so
 * there is no login. One card per paid plan, the selected plan's features, and
 * one Pay button.
 */
export const PlanCheckout: React.FC<Props> = ({ user, plans, plansError, activePlan, loading, onPay }) => {
  const influencerSide = ['Influencer', 'Talent Partner'].includes(user?.account_type);
  const paidPlans = useMemo(() => (plans || []).filter(isPaidPlan), [plans]);
  const activePaid = activePlan && !['FOC', 'Trial'].includes(activePlan.plan_name) ? activePlan : null;

  /**
   * Why a plan can't be bought now ('' = it can). Creators: the same rules as
   * the web's pricing cards (pricing-plan.tsx). Brands: not the plan already
   * auto-renewing — a second purchase would start a second mandate.
   */
  const blockedReason = (plan: any): string => {
    if (influencerSide) {
      if (activePaid && ['Savings', 'Special Savings'].includes(activePaid.plan_name)) {
        return plan.plan_name === 'Savings' ? 'Your current plan' : 'Included in your yearly plan';
      }
      if (activePaid?.plan_name === plan.plan_name) return 'Your current plan';
      return '';
    }
    if (!hasRazorpayPlan(plan)) return 'Not available right now';
    if (activePaid?.plan_name === plan.plan_name && activePaid?.payment_mode === 'razorpay_autopay') return 'Your current plan';
    return '';
  };

  const [selectedId, setSelectedId] = useState<number | null>(null);
  useEffect(() => {
    if (selectedId !== null && paidPlans.some((p) => p.plan_id === selectedId)) return;
    const first = paidPlans.find((p) => !blockedReason(p)) || paidPlans[0];
    setSelectedId(first ? first.plan_id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidPlans, activePlan]);
  const selected = paidPlans.find((p) => p.plan_id === selectedId) || null;
  const selectedBlocked = selected ? blockedReason(selected) : '';

  // Special coupon: creators' yearly plan in India only (as pricing-plan.tsx);
  // the purchase then goes as the one-time discounted order.
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponMsg, setCouponMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const showCoupon =
    !!selected && !selectedBlocked && influencerSide && isYearly(selected) && selected.plan_name !== 'FOC' &&
    String(selected.currency || 'INR').toUpperCase() === 'INR' && Number(user?.influencer_id) > 0;

  // A coupon belongs to the plan it was applied on.
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponMsg(null);
    setCouponInput('');
    setCouponOpen(false);
  }, [selectedId]);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) {
      setCouponMsg({ type: 'error', text: 'Please enter a coupon code.' });
      return;
    }
    setApplyingCoupon(true);
    setCouponMsg(null);
    try {
      const res: any = await subscriptionService.validateCoupon({ coupon_code: code, influencer_id: user.influencer_id });
      const row = res?.data?.rows?.[0];
      if (row?.valid) {
        setAppliedCoupon({ ...row, coupon_code: code });
        setCouponMsg({ type: 'success', text: `Coupon applied! You pay ₹${row.special_amount} for the year.` });
      } else {
        setAppliedCoupon(null);
        setCouponMsg({ type: 'error', text: row?.message || res?.info || 'Invalid coupon code.' });
      }
    } catch {
      setAppliedCoupon(null);
      setCouponMsg({ type: 'error', text: 'Could not check the coupon. Please try again.' });
    } finally {
      setApplyingCoupon(false);
    }
  };

  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const groups = useMemo(() => (selected ? featureGroupsOf(selected, influencerSide) : []), [selected, influencerSide]);
  const totalFeatures = groups.reduce((n, g) => n + g.items.length, 0);
  let featureBudget = showAllFeatures ? Infinity : COLLAPSED_FEATURES;
  const shownGroups = groups
    .map((g) => {
      const items = g.items.slice(0, Math.max(0, featureBudget));
      featureBudget -= items.length;
      return { ...g, items };
    })
    .filter((g) => g.items.length);

  const pay = () => {
    if (!selected || selectedBlocked || loading) return;
    if (appliedCoupon) onPay({ ...selected, amount: appliedCoupon.special_amount }, appliedCoupon.coupon_code);
    else onPay(selected);
  };

  const forName = user?.agency_account_id && user?.brand_id
    ? user?.brand_name
    : user?.account_type === 'Talent Partner' && user?.influencer_type === 'Talent Partner'
      ? user?.name
      : user?.brand_id ? user?.brand_name : '';

  const payAmount = selected ? (appliedCoupon ? appliedCoupon.special_amount : selected.amount) : 0;
  const autoRenews = !!selected && !appliedCoupon && hasRazorpayPlan(selected);
  const taxNote = selected && (selected.gst_included === 'true' || /inclusive/i.test(String(selected.description || '')))
    ? 'inclusive of all taxes'
    : '';
  const payNote = !selected
    ? ''
    : appliedCoupon
      ? 'One-time payment for 1 year · no auto-renewal'
      : autoRenews
        ? `Auto-renews ${periodOf(selected).every}${taxNote ? ` · ${taxNote}` : ''}`
        : `One-time payment for ${Number(selected.days) || 30} days${taxNote ? ` · ${taxNote}` : ''}`;

  return (
    <main className='plan-checkout'>
      <CheckoutLogo />

      <section className='plan-checkout__intro'>
        {forName && <span className='plan-checkout__for'>For {forName}</span>}
        <div className='plan-checkout__icon'><CrownIcon /></div>
        <h1 className='plan-checkout__title'>{activePaid ? "You're on Premium" : 'Go Premium'}</h1>
        <p className='plan-checkout__body'>
          {influencerSide
            ? 'Apply to paid and barter campaigns, negotiate and get Zap Pro.'
            : 'Unlimited campaigns, proposals, creator reports and campaign analytics.'}
        </p>
      </section>

      {activePaid && (
        <div className='plan-checkout__active'>
          <CheckIcon />
          <span>
            Premium active{Number(activePaid.days_counts) > 0 ? ` · ${activePaid.days_counts} days left` : ''}
            {activePaid.payment_mode === 'razorpay_autopay' ? ' · auto-renews' : ''}
          </span>
        </div>
      )}

      {plansError ? (
        <div className='plan-checkout__notice'>{plansError}</div>
      ) : plans === null ? (
        <div className='plan-checkout__plans'>
          <div className='plan-checkout__skeleton' />
          <div className='plan-checkout__skeleton' />
        </div>
      ) : !paidPlans.length ? (
        <div className='plan-checkout__notice'>No plans are available right now. Please go back to the app and try again.</div>
      ) : (
        <>
          <div className='plan-checkout__plans' role='radiogroup' aria-label='Plans'>
            {paidPlans.map((plan) => {
              const on = plan.plan_id === selectedId;
              const blocked = blockedReason(plan);
              const period = periodOf(plan);
              const strike = Number(plan.subscription_amount) > Number(plan.amount);
              const name = plan.plan_name === plan.plan_type ? 'Premium' : plan.plan_name;
              return (
                <button
                  key={plan.plan_id}
                  type='button'
                  role='radio'
                  aria-checked={on}
                  className={`plan-checkout__plan${on ? ' plan-checkout__plan--on' : ''}${blocked ? ' plan-checkout__plan--blocked' : ''}`}
                  onClick={() => setSelectedId(plan.plan_id)}
                >
                  <span className='plan-checkout__radio' aria-hidden />
                  <span className='plan-checkout__plan-main'>
                    <span className='plan-checkout__plan-name'>
                      {name}
                      {Number(plan.discount) > 0 && <span className='plan-checkout__save'>Save {plan.discount}%</span>}
                    </span>
                    <span className='plan-checkout__plan-sub'>
                      {blocked || period.billed}
                      {!blocked && isYearly(plan) && Number(plan.monthly_amount) > 0 && ` · ${money(plan, plan.monthly_amount)}/month`}
                    </span>
                  </span>
                  <span className='plan-checkout__plan-price'>
                    {strike && <span className='plan-checkout__strike'>{money(plan, plan.subscription_amount)}</span>}
                    <span className='plan-checkout__amount'>{money(plan, plan.amount)}</span>
                    <span className='plan-checkout__per'>/{period.per}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {showCoupon && (
            <div className='plan-checkout__coupon'>
              {appliedCoupon ? (
                <div className='plan-checkout__coupon-row'>
                  <span className='plan-checkout__coupon-tag'>{appliedCoupon.coupon_code} applied</span>
                  <button type='button' className='plan-checkout__link' onClick={() => { setAppliedCoupon(null); setCouponMsg(null); setCouponInput(''); }}>
                    Remove
                  </button>
                </div>
              ) : couponOpen ? (
                <div className='plan-checkout__coupon-row'>
                  <input
                    type='text'
                    className='plan-checkout__coupon-input'
                    placeholder='Coupon code'
                    value={couponInput}
                    onChange={(e) => { setCouponInput(e.target.value); setCouponMsg(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(); }}
                    disabled={applyingCoupon}
                    autoFocus
                  />
                  <button type='button' className='plan-checkout__coupon-apply' onClick={applyCoupon} disabled={applyingCoupon || !couponInput.trim()}>
                    {applyingCoupon ? 'Checking…' : 'Apply'}
                  </button>
                </div>
              ) : (
                <button type='button' className='plan-checkout__link' onClick={() => setCouponOpen(true)}>Have a coupon code?</button>
              )}
              {couponMsg && <div className={`plan-checkout__coupon-msg plan-checkout__coupon-msg--${couponMsg.type}`}>{couponMsg.text}</div>}
            </div>
          )}

          {groups.length > 0 && (
            <section className='plan-checkout__features'>
              <h2 className='plan-checkout__features-title'>What you get</h2>
              {shownGroups.map((g, gi) => (
                <div key={gi} className='plan-checkout__group'>
                  <div className='plan-checkout__group-title'>{g.title}</div>
                  <ul>
                    {g.items.map((it, ii) => (
                      <li key={ii}>
                        <CheckIcon />
                        <span className='plan-checkout__feature'>{it.text}</span>
                        {it.value && <span className='plan-checkout__feature-value'>{it.value}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {totalFeatures > COLLAPSED_FEATURES && (
                <button type='button' className='plan-checkout__more' onClick={() => setShowAllFeatures((v) => !v)}>
                  {showAllFeatures ? 'Show less' : `See all ${totalFeatures} features`}
                  <ChevronIcon up={showAllFeatures} />
                </button>
              )}
            </section>
          )}

          <p className='plan-checkout__hint'>After paying, go back to the ClanConnect app to use Premium.</p>

          <div className='plan-checkout__bar'>
            <button type='button' className='plan-checkout__pay' disabled={!selected || !!selectedBlocked || loading} onClick={pay}>
              {loading ? 'Please wait…' : !selected ? 'Choose a plan' : selectedBlocked ? selectedBlocked : `Pay ${money(selected, payAmount)}`}
            </button>
            {payNote && !selectedBlocked && <div className='plan-checkout__pay-note'>{payNote}</div>}
            <div className='plan-checkout__secure'><LockIcon />Secure payment by Razorpay</div>
          </div>
        </>
      )}
    </main>
  );
};
